import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getAccessibleItems, findNavItem } from '../config/navigation';
import { getFrequentPaths } from '../Hooks/useRecentPages';
import { getModifierKey } from '../Hooks/useKeyboardShortcuts';

// Window event other components can dispatch to open the palette
// (e.g. the offcanvas "Search pages" button), avoiding prop drilling.
export const OPEN_PALETTE_EVENT = 'open-command-palette';

// Cmd/Ctrl+K command palette: fuzzy-jump to any page the user can access.
// When the query is empty it shows the user's most frequent pages.
function CommandPalette() {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // Global open triggers: Cmd/Ctrl+K toggles, custom event opens.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShow((prev) => !prev);
      }
    };
    const onOpen = () => setShow(true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  // Fresh query each time it opens.
  useEffect(() => {
    if (show) setQuery('');
  }, [show]);

  // Recompute the accessible set whenever the palette opens (access is stable
  // after login, but this keeps it correct across a re-auth without a reload).
  const allItems = useMemo(() => getAccessibleItems(), [show]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const accessiblePaths = new Set(allItems.map((i) => i.path));
      const frequent = getFrequentPaths(8)
        .filter((p) => accessiblePaths.has(p))
        .map(findNavItem)
        .filter(Boolean);
      return frequent.length ? frequent : allItems;
    }
    return allItems.filter((i) => i.label.toLowerCase().includes(q)
      || (i.keywords || '').toLowerCase().includes(q)
      || i.path.toLowerCase().includes(q));
  }, [query, allItems]);

  // Keep the highlight in range as results change.
  useEffect(() => { setActiveIndex(0); }, [query, show]);

  const close = () => setShow(false);

  const go = (item) => {
    if (!item) return;
    close();
    navigate(item.path);
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[activeIndex]);
    }
  };

  const showingFrequent = !query.trim() && getFrequentPaths(1).length > 0;

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      className="command-palette"
      contentClassName="command-palette-content"
      onEntered={() => inputRef.current?.focus()}
    >
      <div className="cp-input-row">
        <i className="fa fa-search cp-input-icon" />
        <input
          ref={inputRef}
          className="cp-input"
          placeholder="Jump to a page…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
        />
        <span className="cp-esc">esc</span>
      </div>

      <div className="cp-results">
        {!query.trim() && (
          <div className="cp-section-label">{showingFrequent ? 'Frequent' : 'All pages'}</div>
        )}
        {results.length === 0 && (
          <div className="cp-empty">
            No pages match &ldquo;
            {query}
            &rdquo;.
          </div>
        )}
        {results.map((item, idx) => (
          <button
            type="button"
            key={item.path}
            className={`cp-result ${idx === activeIndex ? 'active' : ''}`}
            onMouseEnter={() => setActiveIndex(idx)}
            onClick={() => go(item)}
          >
            <span className="cp-result-icon" style={{ background: item.bg, color: item.color }}>
              <i className={`fa ${item.icon}`} />
            </span>
            <span className="cp-result-label">{item.label}</span>
            {item.shortcut && (
              <span className="cp-result-shortcut">
                {getModifierKey()}
                +
                {item.shortcut.toUpperCase()}
              </span>
            )}
          </button>
        ))}
      </div>
    </Modal>
  );
}

export default CommandPalette;
